<template>
  <div class="card-box">
    <div class="form-group">
      <!-- Title -->
      <h4 class="title">
        <font-awesome-icon :icon="faIcons.faKeySolid" />
        Set Your Account Password
      </h4>
      <span v-if="!resetSuccess">
        Please enter
        <strong>your desired password</strong>
        below. A confirmation email will be sent to you with instructions to
        lock in your new password before it can be used.
      </span>
      <div style="line-height: 80%">
        <br />
      </div>

      <!-- Form Inputs -->
      <div class="control reset-form fade-in-load-login" v-if="!resetSuccess">
        <div
          class="form-group"
          :class="{
            'has-error': resetInfoEntered && !checkEmail(credentials.email),
          }">
          <label class="form-label" for="email-address">
            Your registered e-mail address
          </label>
          <span class="icon form-icon">
            <font-awesome-icon :icon="faIcons.faEnvelope" />
          </span>
          <input
            class="form-input form-icon-offset"
            type="email"
            placeholder="E-mail Address"
            id="email-address"
            v-model="credentials.email" />
          <transition name="fade">
            <div v-if="resetInfoEntered && !checkEmail(credentials.email)">
              <p
                aria-live="assertive"
                style="margin-bottom: -1.3em; color: #b00"
                class="form-input-hint">
                This is not a valid email address.
              </p>
            </div>
          </transition>
        </div>

        <template v-if="!entityIdForEmailDomain">
          <div style="line-height: 40%">
            <br />
          </div>
          <label class="form-label" for="password">Choose a password</label>
          <span class="icon form-icon">
            &nbsp;
            <font-awesome-icon :icon="faIcons.faLock" class="password-icon" />
          </span>
          <PasswordToggle
            id="password"
            inputClass="input-with-icon-offset"
            placeholder="New Password"
            v-model="credentials.password" />
          <div style="line-height: 40%">
            <br />
          </div>
          <label class="form-label" for="password2" style="margin-top: 0.5em">
            Verify your password is correct
          </label>
          <span class="icon form-icon">
            &nbsp;
            <font-awesome-icon :icon="faIcons.faLock" class="password-icon" />
          </span>
          <PasswordToggle
            id="password2"
            inputClass="input-with-icon-offset"
            placeholder="Confirm New Password"
            v-model="credentials.password2" />
          <div style="line-height: 80%">
            <br />
          </div>
        </template>
        <template v-else>
          <p class="error">
            {{ credentials.email.split('@').pop() }} users must sign in through
            their host organization.
          </p>
        </template>
      </div>

      <!-- Errors -->
      <transition name="fade">
        <p
          aria-live="assertive"
          class="form-input-hint error"
          v-if="loginInfoEntered && passwordFailMessage">
          {{ passwordFailMessage }}
        </p>
      </transition>
      <transition name="fade">
        <p
          aria-live="assertive"
          class="form-input-hint error"
          v-if="loginInfoEntered && error">
          {{ error }}
        </p>
      </transition>
      <div style="line-height: 100%">
        <br />
      </div>

      <!-- Submit/Go to Login Buttons -->
      <div id="button-box" v-if="!resetSuccess">
        <div>
          <button
            class="btn btn-primary card-button"
            :class="{
              loading: isLoading,
            }"
            :disabled="
              isLoading ||
              !resetInfoEntered ||
              passwordFailMessage ||
              !checkEmail(credentials.email) ||
              null
            "
            @click="doReset()">
            Request New Password
          </button>
          <div style="text-align: center; margin-top: 1em">
            <button class="btn btn-link" @click="goToLogin()">
              <font-awesome-icon :icon="faIcons.faCaretLeftSolid" />
              Return to main login
            </button>
          </div>
        </div>
      </div>
      <div id="button-box" v-if="resetSuccess">
        <div style="text-align: center; margin-bottom: 2em">
          <div class="success"><h3>Password Reset Requested</h3></div>
          <font-awesome-icon
            :icon="faIcons.faEnvelope"
            class="success"
            size="3x" />
          <p style="margin-top: 1.5em">
            <strong style="font-size: larger; line-height: 1.4em">
              You have been sent an email with instructions to finalize your
              password reset request and lock in your new password.
            </strong>
          </p>
        </div>
        <button
          class="btn btn-primary card-button"
          @click="goToLogin()"
          style="align-text: right">
          Return to main login
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';

import { complexEnough, pw } from 'assets/js/passwords';
import auth from 'assets/js/auth';

import Mixin from './Mixins';
import PasswordToggle from './PasswordInputWithToggle';

const re = /\S+@\S+\.\S+/;

const ERROR_MESSAGES = {
  missing: 'Must provide a password.',
  tooShort: 'Password must be at least 8 characters long',
  common: 'Password may not be a commonly used password.',
  invalidMatch: 'Passwords do not match.',
  alreadyRegistered: 'Email is already registered with QReserve.',
  complexity:
    'Passwords under 12 characters must contain of characters ' +
    'from lowercase, uppercase, numbers, and special characters.',
};

export default {
  name: 'password-reset-card',
  components: {
    PasswordToggle,
  },

  mixins: [Mixin],

  data() {
    return {
      commonPWObject: this.arrayToObject(
        pw.PASSWORDS,
        (k) => k,
        () => true,
      ),
      credentials: {
        email: '',
        password: '',
        password2: '',
      },
      error: '',
      isLoading: false,
      resetSuccess: false,
    };
  },

  created() {
    this.setTitle('Set Your Account Password');
  },

  computed: {
    // loginInfoEntered checks to see if the user has entered something in
    // both the email and password fields.
    loginInfoEntered() {
      return this.credentials.email !== '' && this.credentials.password !== '';
    },

    // passwordFailMessage selects a message to display to the user if an
    // invalid password has been selected.
    passwordFailMessage() {
      const pwd = this.credentials.password;
      const confirmPwd = this.credentials.password2;
      if (!pwd) return ERROR_MESSAGES.missing;
      if (pwd.length < 8) return ERROR_MESSAGES.tooShort;
      if (this.commonPWObject[pwd]) return ERROR_MESSAGES.common;
      if (this.passwordNotComplex) return ERROR_MESSAGES.complexity;
      if (pwd !== confirmPwd) return ERROR_MESSAGES.invalidMatch;
      return null;
    },

    passwordNotComplex() {
      return !complexEnough(this.credentials.password);
    },

    // resetInfoEntered returns whether or not all fields are non-empty,
    // whether or not both password fields are equivalent, and if all errors
    // from the strength test are eliminated.
    resetInfoEntered() {
      return (
        this.credentials.email !== '' &&
        (this.credentials.password !== '' || this.credentials.password2 !== '')
      );
    },
  },

  methods: {
    // checkEmail checks a string to see if it formatted like an email
    // address.
    checkEmail(email) {
      if (_.isUndefined(email)) {
        return true;
      }
      if (email === '') {
        return true;
      }
      if (email.indexOf('@') === -1) {
        return false;
      }
      return re.test(email);
    },

    // doReset connects to the server through the API and sends the password
    // reset in.
    doReset() {
      this.isLoading = true;

      const credentials = {
        email: this.credentials.email,
        password: [this.credentials.password, this.credentials.password2],
        token: this.$route.params.token,
      };

      auth
        .PasswordReset(credentials)
        .then(() => {
          this.isLoading = false;
          this.resetSuccess = true;
          this.credentials.email = '';
          this.credentials.password = '';
          this.credentials.password2 = '';
          this.error = '';
        })
        .catch((error) => {
          this.isLoading = false;
          if (error) this.error = error;
          else this.error = 'An unknown error has occurred.';
        });
    },
  },

  mounted() {
    if (this.$route.query.e) this.credentials.email = this.$route.query.e;
  },

  props: {
    // Functions
    goToLogin: {
      type: Function,
      required: true,
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~assets/css/colors';
@import '~assets/css/partials/login';

.title {
  color: var(--primary-color, $primarycolor);
}

.subtitle {
  color: var(--primary-color, $primarycolor);
  text-align: center;
  margin-top: -5px;
}

span.form-icon {
  position: absolute;
  margin-top: 6px;
  margin-left: 8px;
  z-index: 10;
  opacity: 0.4;
}

.form-icon-offset {
  padding: 12px 28px;
}

.password-icon {
  margin-left: -8px;
}

.error {
  color: $dangercolor;
}

.has-error .form-input-hint {
  color: $dangercolor;
}
</style>
